import { cva } from 'class-variance-authority';
import { AlertTriangle, CheckCircle2, CircleDashed, Loader2, XCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

export type Status = 'ok' | 'degraded' | 'fail' | 'unknown' | 'running';

const badge = cva(
  'inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium whitespace-nowrap',
  {
    variants: {
      status: {
        ok: 'bg-ok-bg text-ok',
        degraded: 'bg-warn-bg text-warn',
        running: 'bg-warn-bg text-warn',
        fail: 'bg-fail-bg text-fail',
        unknown: 'bg-neutral-bg text-muted',
      },
    },
  },
);

const LABEL: Record<Status, string> = {
  ok: 'Healthy',
  degraded: 'Degraded',
  running: 'Running',
  fail: 'Failing',
  unknown: 'Unknown',
};

const ICON = {
  ok: CheckCircle2,
  degraded: AlertTriangle,
  running: Loader2,
  fail: XCircle,
  unknown: CircleDashed,
};

/** Status is always conveyed by icon + text, never colour alone (WCAG 1.4.1). */
export function StatusBadge({
  status,
  label,
  className,
}: {
  status: Status;
  label?: string;
  className?: string;
}) {
  const Icon = ICON[status];
  return (
    <span className={cn(badge({ status }), className)}>
      <Icon aria-hidden="true" className={cn('size-3.5', status === 'running' && 'animate-spin')} />
      {label ?? LABEL[status]}
    </span>
  );
}

/** Maps domain statuses (execution, step, audit result, credential) to badge semantics. */
export function DomainStatus({ value }: { value: string }) {
  const map: Record<string, Status> = {
    succeeded: 'ok',
    success: 'ok',
    active: 'ok',
    ok: 'ok',
    running: 'running',
    waiting: 'degraded',
    rejected: 'fail',
    pending: 'unknown',
    failed: 'fail',
    failure: 'fail',
    denied: 'fail',
    revoked: 'fail',
    fail: 'fail',
    cancelled: 'unknown',
    skipped: 'unknown',
    degraded: 'degraded',
    refused: 'fail',
    completed: 'ok',
    stopped: 'degraded',
    draft: 'unknown',
    testing: 'degraded',
    published: 'ok',
    deprecated: 'unknown',
    discovered: 'unknown',
    suspended: 'degraded',
    approved: 'ok',
    disabled: 'unknown',
    verified: 'ok',
    inferred: 'degraded',
    unknown: 'unknown',
    not_found: 'unknown',
    requires_confirmation: 'degraded',
    conflict: 'fail',
  };
  return (
    <StatusBadge
      status={map[value] ?? 'unknown'}
      label={value.charAt(0).toUpperCase() + value.slice(1)}
    />
  );
}
